import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { isSameDay } from 'date-fns'
import { useState, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Button, IconButton, InputAdornment, Popover, TextField, useTheme } from '@mui/material'
import { DateCalendar, PickersDay, PickersDayProps } from '@mui/x-date-pickers'

import { Icon } from '@iconify/react'

export interface DateRange {
  startDate: Date | null
  endDate: Date | null
}

interface DateRangePickerProps {
  value: DateRange | null
  onChange: (dateRange: DateRange) => void
  minDate?: Date
  maxDate?: Date
  showLabel?: boolean
  showCalendar?: boolean
  displayValue?: (startDate: Date | null, endDate: Date | null) => string
  handleClear?: () => void
  iconOnly?: boolean
}

const defaultDisplayValue = (startDate: Date | null, endDate: Date | null): string => {
  if (!startDate || !endDate) return ''
  return `${formatThaiShortDateTwoDigitYear(startDate)} - ${formatThaiShortDateTwoDigitYear(endDate)}`
}

const DateRangePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  showLabel = true,
  showCalendar = true,
  displayValue = defaultDisplayValue,
  handleClear: onClear,
  iconOnly = false,
}: DateRangePickerProps) => {
  const { t } = useTranslation('common')
  const theme = useTheme()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [tempStartDate, setTempStartDate] = useState<Date | null>(value?.startDate ?? null)
  const [tempEndDate, setTempEndDate] = useState<Date | null>(value?.endDate ?? null)

  const open = Boolean(anchorEl)

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setTempStartDate(value?.startDate ?? null)
    setTempEndDate(value?.endDate ?? null)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleDateChange = (date: Date | null) => {
    if (!date) return

    // Clamp date to min/max
    if (minDate && date < minDate) date = minDate
    if (maxDate && date > maxDate) date = maxDate

    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(date)
      setTempEndDate(null)
    } else if (date < tempStartDate) {
      setTempStartDate(date)
    } else {
      setTempEndDate(date)
    }
  }

  const handleApply = () => {
    const today = new Date()
    onChange({ startDate: tempStartDate ?? today, endDate: tempEndDate ?? today })
    handleClose()
  }

  const handleClear = () => {
    setTempStartDate(null)
    setTempEndDate(null)

    if (onClear) {
      onClear()
      handleClose()
    } else {
      onChange({ startDate: null, endDate: null })
    }
  }

  const CustomDay = (props: PickersDayProps<Date>) => {
    const { day, outsideCurrentMonth, ...other } = props
    const isStartDate = tempStartDate && isSameDay(day, tempStartDate)
    const isEndDate = tempEndDate && isSameDay(day, tempEndDate)
    const isInRange = tempStartDate && tempEndDate && day >= tempStartDate && day <= tempEndDate

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{
          backgroundColor:
            isStartDate || isEndDate || isInRange ? theme.palette.primary.lighter : undefined,
        }}
      />
    )
  }

  return (
    <Box>
      {iconOnly ? (
        <IconButton
          onClick={handleOpen}
          sx={{
            width: 40,
            height: 40,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.primary',
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Icon icon="majesticons:calendar" width={24} height={24} />
        </IconButton>
      ) : (
        <TextField
          {...(showLabel ? { label: t('select.date.range') } : {})}
          value={displayValue(value?.startDate ?? null, value?.endDate ?? null)}
          onClick={handleOpen}
          inputProps={{ readOnly: true }}
          fullWidth
          InputProps={
            showCalendar
              ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon icon="majesticons:calendar" width={24} height={24} />
                  </InputAdornment>
                ),
              }
              : undefined
          }
        />
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2}>
          <DateCalendar
            value={tempStartDate}
            onChange={handleDateChange}
            slots={{ day: CustomDay }}
            minDate={minDate}
            maxDate={maxDate}
          />
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" color="inherit" onClick={handleClear}>
              {t('clear')}
            </Button>
            <Button variant="contained" onClick={handleApply}>
              {t('confirm')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default DateRangePicker